import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Drop, Heart, Users, Package } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const PartySmartPage = () => {
  const navigate = useNavigate();
  const [checkedItems, setCheckedItems] = useState([]);

  const partyChecklist = [
    {
      category: '💧 Hydration',
      emoji: '💦',
      items: [
        'Water bottles (bring extra!)',
        'Electrolyte drinks',
        'Set phone reminders to drink water every hour'
      ]
    },
    {
      category: '🛡️ Protection & Safety',
      emoji: '✅',
      items: [
        'Condoms (internal & external)',
        'Lube (water or silicone-based)',
        'Clean supplies for safer use',
        'Naloxone/Narcan kit'
      ]
    },
    {
      category: '💙 Consent & Boundaries',
      emoji: '🤝',
      items: [
        'Check in with yourself about your boundaries',
        'Practice saying "no" - it\'s always okay!',
        'Ask for consent before touching anyone',
        'Respect others\' boundaries'
      ]
    },
    {
      category: '🎒 Essentials',
      emoji: '📦',
      items: [
        'Phone charger',
        'Emergency contacts list',
        'ID & health card',
        'Snacks (easy to eat)',
        'Comfortable clothes to change into'
      ]
    },
    {
      category: '🧠 Harm Reduction',
      emoji: '⚡',
      items: [
        'Start low, go slow with any substances',
        'Don\'t mix substances without knowing effects',
        'Tell a trusted friend where you\'re going',
        'Have a safe way to get home',
        'Test your substances if possible'
      ]
    }
  ];

  const toggleItem = (item) => {
    if (checkedItems.includes(item)) {
      setCheckedItems(checkedItems.filter(i => i !== item));
    } else {
      setCheckedItems([...checkedItems, item]);
      toast.success('✅ Checked off!', { duration: 1000 });
    }
  };

  const totalItems = partyChecklist.reduce((acc, cat) => acc + cat.items.length, 0);
  const progress = Math.round((checkedItems.length / totalItems) * 100);

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      {/* Header */}
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              data-testid="back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <div className="flex items-center space-x-2">
              <span className="text-3xl emoji-bounce">🥳</span>
              <h1 className="text-2xl font-bold text-gradient">
                Party Smart Mode
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        {/* Intro Card */}
        <div className="mb-8 p-6 party-gradient rounded-3xl text-white">
          <div className="text-center">
            <div className="text-5xl mb-3">✨</div>
            <h2 className="text-2xl font-bold mb-3">Let's Party Safer Together!</h2>
            <p className="text-white/90 leading-relaxed">
              No judgment, just real tips to help you have a great time while staying safe. 
              Check off what you've got ready! 💜
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        {checkedItems.length > 0 && (
          <div className="mb-6 p-4 bg-[rgb(var(--surface))] rounded-2xl border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-[rgb(var(--text-primary))]">
                Your Progress 🎯
              </span>
              <span className="text-sm font-bold text-[rgb(var(--party))]">{progress}%</span>
            </div>
            <div className="w-full bg-[rgb(var(--background))] rounded-full h-3">
              <div 
                className="h-3 rounded-full party-gradient transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Checklist */}
        <div className="space-y-6">
          {partyChecklist.map((category, catIndex) => (
            <div key={catIndex} className="space-y-3">
              <h3 className="text-lg font-bold text-[rgb(var(--text-primary))] flex items-center">
                <span className="text-2xl mr-2">{category.emoji}</span>
                {category.category}
              </h3>
              <div className="space-y-2">
                {category.items.map((item, itemIndex) => {
                  const isChecked = checkedItems.includes(item);
                  return (
                    <button
                      key={itemIndex}
                      onClick={() => toggleItem(item)}
                      data-testid={`checklist-item-${catIndex}-${itemIndex}`}
                      className={`w-full flex items-start p-4 rounded-2xl border transition-all duration-200 text-left ${                        isChecked                           ? 'bg-[rgb(var(--primary))]/20 border-[rgb(var(--primary))]/50'                           : 'bg-[rgb(var(--surface))] border-white/5 hover:border-[rgb(var(--primary))]/30'                      }`}
                    >
                      <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center mr-3 mt-0.5 transition-all ${                        isChecked                           ? 'border-[rgb(var(--primary))] bg-[rgb(var(--primary))]'                           : 'border-[rgb(var(--text-secondary))]'                      }`}>
                        {isChecked && (
                          <CheckCircle size={16} weight="fill" className="text-white" />
                        )}
                      </div>
                      <span className={`flex-1 leading-relaxed ${                        isChecked                           ? 'text-[rgb(var(--text-primary))] font-semibold'                           : 'text-[rgb(var(--text-secondary))]'                      }`}>
                        {item}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Hydration Reminder */}
        <div className="mt-8 p-6 bg-cyan-500/10 border border-cyan-500/30 rounded-3xl">
          <div className="flex items-start">
            <Drop size={32} weight="duotone" className="text-cyan-400 mr-4 flex-shrink-0 emoji-bounce" />
            <div>
              <h3 className="text-lg font-bold text-[rgb(var(--text-primary))] mb-2">
                💦 Hydration Reminder
              </h3>
              <p className="text-[rgb(var(--text-secondary))] text-sm leading-relaxed mb-3">
                Set a timer on your phone to drink water every hour. Your body will thank you! 
                Dehydration can make you feel worse and is risky when partying.
              </p>
              <div className="flex items-center text-xs text-cyan-400 font-semibold">
                <Heart size={14} className="mr-1" />
                Pro tip: Alternate between water and your drink of choice
              </div>
            </div>
          </div>
        </div>

        {/* Final Encouragement */}
        {checkedItems.length === totalItems && (
          <div className="mt-8 p-6 party-gradient rounded-3xl text-white text-center">
            <div className="text-5xl mb-3">🎉</div>
            <h3 className="text-2xl font-bold mb-2">You're All Set!</h3>
            <p className="text-white/90">
              Have an amazing time and stay safe! You've got this! 💜✨
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-8 space-y-3">
          <Button
            onClick={() => navigate('/chat')}
            className="w-full min-h-[56px] rounded-full party-gradient text-white font-bold"
          >
            <span className="text-xl mr-2">💬</span>
            Chat with Nad for More Tips
          </Button>
          
          <Button
            onClick={() => navigate('/harm-reduction-kit')}
            variant="outline"
            className="w-full min-h-[56px] rounded-full border-[rgb(var(--primary))]/50 text-[rgb(var(--primary))] hover:bg-[rgb(var(--primary))]/10"
          >
            <span className="text-xl mr-2">🧰</span>
            View Full Harm Reduction Kit
          </Button>
        </div>
      </main>
    </div>
  );
};

export default PartySmartPage;
